import * as THREE from 'three';

import Stats from 'three/addons/libs/stats.module.js';
import { GUI } from 'three/addons/libs/lil-gui.module.min.js';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';


import Store from '../Store';



class ColorGradient {
  constructor(colors) {
    // Assuming colors is an array of hex color values
    this.colors = colors.map(color => new THREE.Color(color));
  }

  getColorAt(position) {
    // Ensure position is between 0 and 1
    position = THREE.MathUtils.clamp(position, 0, 1);

    // Find which two colors to blend
    let colorIndex = (this.colors.length - 1) * position;
    let index1 = Math.floor(colorIndex);
    let index2 = Math.min(index1 + 1, this.colors.length - 1);

    // Calculate the weight of each color
    let weight2 = colorIndex - index1;
    let weight1 = 1 - weight2;

    // Linearly interpolate the colors
    let color = this.colors[index1].clone().lerp(this.colors[index2], weight2);

    return color;
  }
}



export default class FlowerSparkles {
  constructor(flower) {
    this.flower = flower;
    this.inited = false;
    this.defaults = {
      color1: 0xF7E749,
      color2: 0xF3A644,
      color3: 0xeae1c3,
      radius1: 0,
      radius2: 15,
      segments: 30,
      noiseTimeScale: 20,
      noiseScale: 0.3,
      noiseStrength: 0.14,
      vLimit: 0.8,
    };

    const { scene, camera, } = Store.getState();

    this.colorGradient = new ColorGradient([this.defaults.color1, this.defaults.color2, this.defaults.color3]);
    this.particles = new Array(this.defaults.segments);
    this.uniforms = {
      uTime: {
        value: 0
      },
      uRatio: { value: new THREE.Vector2(window.innerWidth / window.innerHeight, 1) },
      uSize: { value: new THREE.Vector2(this.defaults.radius1, this.defaults.radius2) },
      uPoints: {
        value: new Array(this.defaults.segments * 2).fill().map(() => new THREE.Vector3())
      },
      uColors: {
        value: new Array(this.defaults.segments).fill().map(() => this.colorGradient.getColorAt(THREE.MathUtils.randFloat(0, 1)))
      }
    };

    this.clock = new THREE.Clock(),
    this.screenSize = new THREE.Vector3(),
    this.projectionMatrix = new THREE.Matrix4(),
    this.transformationMatrix = new THREE.Matrix4(),

    this.init();
  }

  init() {
    this.setupParticles();
    this.adjustUniformsBasedOnAspectRatio();
    window.addEventListener("resize", this.onResize.bind(this))
    this.inited = true;
  }

  getNoise(position, scale, time) {
    return new THREE.Vector3(Math.random(), Math.random(), Math.random()).subScalar(0.5).multiplyScalar(0.05);
  }

  setupParticles() {
    const { scene, camera, } = Store.getState();

    // Populate the particles array with particle properties
    for (let i = 0; i < this.defaults.segments; i++) {
        const randomPosition = new THREE.Vector3(
            THREE.MathUtils.randFloatSpread(1),
            0,
            THREE.MathUtils.randFloatSpread(1)
        );
        // Add the flower's mesh position to the random position
        const startPosition = randomPosition.add(this.flower.mesh.position);

        this.particles[i] = {
            position: startPosition,
            velocity: new THREE.Vector3(THREE.MathUtils.randFloatSpread(0.01), THREE.MathUtils.randFloatSpread(0.01), THREE.MathUtils.randFloatSpread(0.01)),
            life: THREE.MathUtils.randFloat(0, 2),
            color: this.colorGradient.getColorAt(THREE.MathUtils.randFloat(0, 1))
        };
    }
    // Now, setup the geometry and material for the THREE.Points
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.defaults.segments * 3);
    const _colors = [];
    this.uniforms.uColors.value.forEach((a) => {
        _colors.push(a.r); 
        _colors.push(a.g); 
        _colors.push(a.b);
      });
    const colors = new Float32Array(_colors);

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('customColor', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.ShaderMaterial({
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      uniforms: this.uniforms,
      defines: {
        NUM_SEGMENTS: this.defaults.segments, 
        NUM_POINTS: this.defaults.segments * 2,
      },
      vertexShader: `
        attribute vec3 customColor;
        varying vec2 vUv;
        varying vec3 vColor;

        void main() {
          vUv = uv;
          vColor = customColor;
          gl_Position = projectionMatrix *  viewMatrix  * vec4( position, 1.0 );
          gl_PointSize = 4.0; 
        }
      `,
      fragmentShader: `
        uniform vec2 uRatio;
        uniform vec2 uSize;
        uniform vec3 uPoints[NUM_POINTS];
        uniform vec3 uColors[NUM_SEGMENTS];
        varying vec2 vUv;
        varying vec3 vColor;

        float sdSegment(in vec2 p, in vec2 a, in vec2 b) {
          vec2 pa = p - a, ba = b - a;
          float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
          return length(pa - ba*h);
        }

        void main() {
          vec2 pos = (vUv - 0.5) * uRatio;
          vec2 center = vec2(0.5, 0.5);
          float distanceFromCenter = distance(gl_PointCoord, center);

          if (distanceFromCenter > 0.5) {
            discard; // This discards pixels outside the circle radius
          }

          vec3 col = vec3(0.0);
          for(int i = 0; i < NUM_POINTS; i++) {
            vec3 p1 = uPoints[i * 2];
            vec3 p2 = uPoints[i * 2 + 1];

            if ((p1.z < -1.0 || p1.z > 1.0) || (p2.z < -1.0 || p2.z > 1.0)) continue;

            float dist = sdSegment(pos, p1.xy, p2.xy);
            // if (dist > 0.25) continue;
            float glow = uSize.y / dist;
            col += glow * vColor;
          }
          gl_FragColor = vec4(col, 1.0); 
        }
      `
    });

    // Create the THREE.Points instance and add to the scene
    this.particleSystem = new THREE.Points(geometry, material);
    this.particleSystem.renderOrder = 999;
    scene.add(this.particleSystem);
  }

  update() {
    if (!this.inited || !this.particleSystem || !this.particleSystem.geometry) return;

    const { scene, camera, } = Store.getState();

    this.uniforms.uTime.value += this.clock.getDelta() * this.defaults.noiseTimeScale;

    this.projectionMatrix.copy(camera.projectionMatrix).multiply(camera.matrixWorldInverse).multiply(this.transformationMatrix);

    const positions = this.particleSystem.geometry.attributes.position.array;
    const colors = this.particleSystem.geometry.attributes.customColor.array;
    
    for (let i = 0; i < this.defaults.segments; i++) {
      const particle = this.particles[i];
      const firstPoint = this.uniforms.uPoints.value[i * 2];
      const secondPoint = this.uniforms.uPoints.value[i * 2 + 1];

      if (particle.life < 0) {
          // particle.life = THREE.MathUtils.randFloat(0.25, 1);
          // particle.position.set(THREE.MathUtils.randFloatSpread(1), -1.5, THREE.MathUtils.randFloatSpread(1));
          // particle.velocity.set(0, 0.05, 0);
      }

      const noise = this.getNoise(
          [particle.position.x * this.defaults.noiseScale, particle.position.y * this.defaults.noiseScale, particle.position.z * this.defaults.noiseScale], 
          [0, 0, 0],
          this.uniforms.uTime.value
      );


      particle.velocity.x += noise.x * this.defaults.noiseStrength;
      particle.velocity.y += noise.y * this.defaults.noiseStrength;
      particle.velocity.z += noise.z * this.defaults.noiseStrength;
      particle.velocity.clampLength(0, this.defaults.vLimit);
      particle.life -= 0.03;

      const tempPosition = particle.position.clone();
      tempPosition.applyMatrix4(this.projectionMatrix);
      secondPoint.copy(tempPosition);
      particle.position.add(particle.velocity);

      tempPosition.copy(particle.position);
      tempPosition.applyMatrix4(this.projectionMatrix);
      firstPoint.copy(tempPosition);

      positions[i * 3] = particle.position.x;
      positions[i * 3 + 1] = particle.position.y;
      positions[i * 3 + 2] = particle.position.z;

      const customColor = particle.color.clone().multiplyScalar(particle.life);
      colors[i * 3] = customColor.r;
      colors[i * 3 + 1] = customColor.g;
      colors[i * 3 + 2] = customColor.b;
    }

    this.particleSystem.geometry.attributes.position.needsUpdate = true;
    this.particleSystem.geometry.attributes.customColor.needsUpdate = true;
  }

  onResize() {
      this.adjustUniformsBasedOnAspectRatio();
  }

  adjustUniformsBasedOnAspectRatio() {
    // Variables h and p are the window's width and height.
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Set the uniform uSize (f) based on t.radius1 and t.radius2 values.
    this.uniforms.uSize.value.set(this.defaults.radius1, this.defaults.radius2);

    if (windowWidth >= windowHeight) {
        // If width is greater than or equal to height, set the aspect ratio accordingly.
        this.uniforms.uRatio.value.set(1, windowHeight / windowWidth);

        // Scale uSize based on width.
        this.uniforms.uSize.value.multiplyScalar(1 / windowWidth);
    } else {
        // If height is greater than width, set the aspect ratio accordingly.
        this.uniforms.uRatio.value.set(windowWidth / windowHeight, 1);

        // Scale uSize based on height.
        this.uniforms.uSize.value.multiplyScalar(1 / windowHeight);
    }
  }

  dispose() {
    // Remove the particle system from the scene
    const { scene } = Store.getState();
    if (this.particleSystem) {
      scene.remove(this.particleSystem);
      if (this.particleSystem.geometry) {
        this.particleSystem.geometry.dispose();
      }
      if (this.particleSystem.material) {
        this.particleSystem.material.dispose();
      }
      this.particleSystem = null;
    }

    // Remove event listener
    window.removeEventListener("resize", this.onResize);

    // Reset inited flag
    this.inited = false;
  }


}

